import { useMemo } from 'react';

import {
  EventCard,
  EventsCarouselView,
  filterEventsByPricing,
  isFreeEvent,
  planEventQuery,
  type BlockComponentProps,
  type EventsCarouselBlockProps,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/content-blocks';

import { useEvents } from '@Hooks/useEvents';
import { useTicketedEvents } from '@Hooks/useTicketedEvents';
import { getCloudinaryImageThumbnailUrl } from '@Utils/cloudinary';
import { unwrapList } from '@Utils/unwrap';

/**
 * Admin-only renderer for the `eventsCarousel` block: fetches the REAL
 * upcoming events exactly the way the membership app's carousel does — the
 * `ticketed-events` and plain `events` endpoints in parallel with the same
 * baseline (published, not archived, not private, Toast-bookable venues),
 * narrowed by the block's authored `filters` prop through the same kit
 * planner, merged and sorted by start time — and renders them with the
 * kit's official EventsCarouselView/EventCard, so preview and app share
 * one presentation. Cards are inert (no linkComponent) and re-filter live
 * as the author edits the heading and filters.
 */

/**
 * The fields the preview needs, common to the admin's Event and
 * TicketedEvent types (plain events carry no ticketTypes — which is
 * exactly what marks them "Free" in the membership app).
 */
type PreviewEvent = {
  id: string;
  name: string;
  startDateTime: string | null;
  visibility: string;
  featuredImage?: { cloudinaryId: string } | null;
  venue?: { name: string } | null;
  ticketTypes?: unknown[];
};

/**
 * Same constraints as membership's EventCarousel (flat JSON:API params),
 * same two endpoints, same merge, same filter planning.
 */
const useUpcomingEvents = (filters: unknown) => {
  // Memoized so the timestamp doesn't change the query keys every render.
  const now = useMemo(() => new Date(), []);

  const plan = useMemo(() => planEventQuery(filters, now), [filters, now]);

  const searchParams = {
    'page[number]': 1,
    'page[size]': 100,
    'include': 'venue,featuredImage,ticketTypes',
    'sort': 'startDateTime',
    'filter[status]': 'PUBLISHED',
    'filter[archivedAt]': 'null',
    // Authored audience REPLACES the baseline "anything but private".
    'filter[visibility]': plan.visibility ?? '!PRIVATE',
    'filter[endDateTime][gte]': now.toISOString(),
    'filter[venue][toastRestaurantGuid]': '!null',
    'filter[venue][toastTicketedEventMenuGuid]': '!null',
    'filter[venue][toastTicketedEventDiningOptionGuid]': '!null',
    'filter[venue][disabledAt]': 'null',
    ...plan.params,
  };

  const ticketedQuery = useTicketedEvents(
    { searchParams },
    { staleTime: 60_000 },
  );
  const basicQuery = useEvents({ searchParams }, { staleTime: 60_000 });

  const events: PreviewEvent[] = useMemo(() => {
    const fetched = [
      ...unwrapList(ticketedQuery.data),
      ...unwrapList(basicQuery.data),
    ] as PreviewEvent[];
    const merged = filterEventsByPricing(fetched, plan.pricing);
    merged.sort((a, b) => {
      if (!a.startDateTime) return 1;
      if (!b.startDateTime) return -1;
      return (
        new Date(a.startDateTime).getTime() -
        new Date(b.startDateTime).getTime()
      );
    });
    return merged.slice(0, plan.limit ?? Infinity);
  }, [ticketedQuery.data, basicQuery.data, plan.pricing, plan.limit]);

  return {
    events,
    isPending: ticketedQuery.isPending || basicQuery.isPending,
  };
};

export default function EventsCarouselPreview({ node }: BlockComponentProps) {
  const { title, filters } = node.props as EventsCarouselBlockProps;
  const { events, isPending } = useUpcomingEvents(filters);

  return (
    <EventsCarouselView
      title={title}
      events={events}
      isPending={isPending}
      // The app renders nothing here; the editor shows a hint so the block
      // doesn't read as broken or missing.
      emptyState={
        <div className="pl-4 text-sm text-zinc-500 italic">
          No upcoming events match these filters.
        </div>
      }
      renderCard={(event) => (
        <EventCard
          event={{
            id: event.id,
            name: event.name,
            startDateTime: event.startDateTime,
            venueName: event.venue?.name,
            free: isFreeEvent(event),
          }}
          image={
            event.featuredImage?.cloudinaryId ? (
              <img
                src={getCloudinaryImageThumbnailUrl(
                  event.featuredImage.cloudinaryId,
                  { width: 512, height: 224 },
                )}
                alt={event.name}
                className="h-full w-full object-cover object-center"
              />
            ) : undefined
          }
        />
      )}
    />
  );
}
